/**
 * @file   src\Pages\ManageUser.tsx
 * @brief  Manage User page.
 * @date   SEP, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
  Container,
  Table,
  Badge,
  useOutletContext,
  useNavigate,
  useRef,
  useState,
  useLocation,
  Pagination,
  useEffect,
  useParams,
  Breadcrumb,
  Modal,
  React,
  Dropdown,
  Button,
  Link,
  InputGroup,
  Row,
  Col,
  OverlayTrigger,
  Tooltip, 
  Accordion, 
} from "../utils/thirdpartyComponents";
import CWSearchinput from "../components/CWSearchinput";
import { AppContextType } from "../interfaces/generalInterface";
import { pageURLs } from "../utils/pageUrls";
import SmallLoader from "../components/SmallLoader";
import {
  CarInfo,
  CarWashSubscription,
  UserCarInfoApiItem,
} from "../interfaces/subscriptionInterface";
import axios from "../config/axiosConfig";
import { CompanyApi, UsersApi } from "../utils/apiUrls";
import { UserItem } from "../interfaces/userInterface";
import { SubscriptionStatus } from "../utils/enums";
import { userType } from "../utils/enums";
import CWRadio from "../components/CWRadio";
import CWCheckbox from "../components/CWCheckbox";
import FilterIcon from "../assets/images/icons/filter";
import { ChangeEvent } from "react";
import Select, { MultiValue } from "react-select";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import AddIcon from '../assets/images/icons/addicon';
import {
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Heading,
  type Editor,
} from "ckeditor5";
import ClassicEditor from "../utils/classicEditor";
import "ckeditor5/ckeditor5.css";
import * as Yup from "yup";
import { sendMessageSchema } from "../validations/sendmessage_pwausers";
import { MessageToaster } from "../utils/toastUtil";
import CWInput from "../components/CWInput";
import CWSelect from "../components/CWSelect";
import { recordCounts } from "../utils/enums";
import CWSwitch from "../components/CWSwitch";
import EditIcon from "../assets/images/icons/editicon";
import { formatPhoneNumber, formatPhoneNumberforApi, getFromLocalStorage } from "../utils/helper";
import { PHONE_MAX_LENGTH, PHONE_REGEX } from "../utils/constants";

  

const ManageUsers = (props: any) => {
  // Access the context
  const { updateData } = useOutletContext<AppContextType>();
  const carwash_owner =
    props.userType === userType["CARWASH-OWNER"] ? true : false;
  const storageData = getFromLocalStorage('CARWASH_OWNER_DATA');
  const userInfo = storageData && storageData !== null ? storageData : null;
  const isLocationManager = userInfo?.userTypeId === 4 && userInfo?.permissionType === 2;
  const manageUsersTableColumnCount = carwash_owner ? 9 : 8;
  // Instantiate MessageToaster
  const toaster = new MessageToaster();

  const [filterClass, setFilterClass] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOption, setSelectedOption] = useState("all");
  const [temporaryOption, setTemporaryOption] = useState(selectedOption);
  const [temporaryOptionvalue, setTemporaryOptionvalue] = useState("");
  const [isCarInfoRequired,setIsCarInfoRequired] = useState(false);
  const [userCarInfoList, setUserCarInfoList] = useState<CarInfo[]>([]);
  const [showCarModal, setShowCarModal] = useState(false);
  const [showPhoneModal,setShowPhoneModal]= useState(false);
  const [currentPhonenumber,setCurrentPhonenumber]= useState("");
  const [phoneError,setPhoneError] = useState("");
  const [userId,setUserId]= useState(0);
  const [isOptSpotEnabled, setIsOptSpotEnabled] = useState(false);
  const [hasOptSpotFeature, setHasOptSpotFeature] = useState(false);

  const [showAddinfoModal, setShowAddinfoModal] = useState(false);
  const [showCarDeleteModal, setShowCarDeleteModal] = useState(false);
  const [carToDelete, setCarToDelete] = useState<CarInfo | null>(null);
  const [carMake, setCarMake] = useState("");
  const [carModel, setCarModel] = useState("");
  const [carColor, setCarColor] = useState("");
  const [carLicensePlate, setCarLicensePlate] = useState("");
  const [editingCarId, setEditingCarId] = useState<number | null>(null);
  const [addCarInfoErrors, setAddCarInfoErrors] = useState({
    carMake: "",
    carModel: "",
    carColor: "",
    carLicensePlate: "",
  });

  const resetAddCarInfoForm = () => {
    setCarMake("");
    setCarModel("");
    setCarColor("");
    setCarLicensePlate("");
    setEditingCarId(null);
    setAddCarInfoErrors({
      carMake: "",
      carModel: "",
      carColor: "",
      carLicensePlate: "",
    });
  };

  const handleCloseAddinfoModal = () => {
    setShowAddinfoModal(false);
    resetAddCarInfoForm();
  };

  const handleOpenAddinfoModal = () => {
    resetAddCarInfoForm();
    setShowAddinfoModal(true);
  };

  const mapToCarInfo = (item: UserCarInfoApiItem): CarInfo => ({
    carId: item.CarID,
    carMake: item.CarMake ?? "",
    carModel: item.CarModel ?? "",
    carColor: item.CarColor ?? "",
    licensePlate: item.CarLicensePlateNumber ?? "",
  });

  const fetchUserCarInfo = async (userIdValue: number) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${CompanyApi.GET_USER_CAR_INFO}${userIdValue}`
      );

      if (res?.data?.error === 0 && Array.isArray(res.data?.data)) {
        setUserCarInfoList(
          res.data.data.map((item: UserCarInfoApiItem) => mapToCarInfo(item))
        );
      } else if (res?.data?.error != 0) {
        setUserCarInfoList([]);
        toaster.toastError(
          res?.data?.message || "Failed to fetch car information"
        );
      }
    } catch (err) {
      setUserCarInfoList([]);
      toaster.toastError("API error while fetching car information");
    } finally {
      setLoading(false);
    }
  };

  const handleShowAddinfoModal = (car: CarInfo) => {
    if (!car.carId) {
      toaster.toastError("Car ID is missing");
      return;
    }
    setEditingCarId(car.carId);
    setCarMake(car.carMake ?? "");
    setCarModel(car.carModel ?? "");
    setCarColor(car.carColor ?? "");
    setCarLicensePlate(car.licensePlate ?? "");
    setAddCarInfoErrors({
      carMake: "",
      carModel: "",
      carColor: "",
      carLicensePlate: "",
    });
    setShowAddinfoModal(true);
  };

  const validateAddCarInfoForm = () => {
    const errors = {
      carMake: carMake.trim() ? "" : "Make is required",
      carModel: carModel.trim() ? "" : "Model is required",
      carColor: carColor.trim() ? "" : "Color is required",
      carLicensePlate: carLicensePlate.trim()
        ? ""
        : "License plate number is required",
    };
    setAddCarInfoErrors(errors);
    return !Object.values(errors).some((error) => error !== "");
  };

  const saveCarInfo = async () => {
    if (!validateAddCarInfoForm()) {
      return;
    }

    const isEditMode = editingCarId !== null;

    try {
      setLoading(true);
      const payload = isEditMode
        ? {
            userId,
            carId: editingCarId,
            carMake: carMake.trim(),
            carModel: carModel.trim(),
            carColor: carColor.trim(),
            carLicensePlatNumber: carLicensePlate.trim(),
          }
        : {
            userId,
            carMake: carMake.trim(),
            carModel: carModel.trim(),
            carColor: carColor.trim(),
            carLicensePlatNumber: carLicensePlate.trim(),
          };

      const res = await axios.post(
        isEditMode
          ? CompanyApi.EDIT_USER_CAR_INFO
          : CompanyApi.ADD_USER_CAR_INFO,
        payload
      );

      if (res?.data?.error === 0) {
        toaster.toastSuccess(
          res?.data?.message ||
            (isEditMode
              ? "Car info updated successfully"
              : "Car info added successfully")
        );
        handleCloseAddinfoModal();
        await fetchUserCarInfo(userId);
        getPWAUsers(currentPage);
      } else {
        toaster.toastError(
          res?.data?.message ||
            (isEditMode ? "Failed to update car info" : "Failed to add car info")
        );
      }
    } catch (err) {
      toaster.toastError(
        isEditMode
          ? "API error while updating car info"
          : "API error while adding car info"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCarClick = (car: CarInfo) => {
    if (!car.carId) {
      toaster.toastError("Please try again later");
      return;
    }
    setCarToDelete(car);
    setShowCarDeleteModal(true);
  };

  const hideCarDeleteModal = () => {
    setShowCarDeleteModal(false);
    setCarToDelete(null);
  };

  const confirmDeleteUserCarInfo = async () => {
    if (!carToDelete?.carId) {
      toaster.toastError("Please try again later");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        userId,
        carId: carToDelete.carId,
      };

      const res = await axios.post(CompanyApi.DELETE_USER_CAR_INFO, payload);

      if (res?.data?.error === 0) {
        toaster.toastSuccess(
          res?.data?.message || "Car info deleted successfully"
        );
        hideCarDeleteModal();
        await fetchUserCarInfo(userId);
        getPWAUsers(currentPage);
      } else {
        toaster.toastError(res?.data?.message || "Failed to delete car info");
        hideCarDeleteModal();
      }
    } catch (err) {
      toaster.toastError("API error while deleting car info");
      hideCarDeleteModal();
    } finally {
      setLoading(false);
    }
  };

  const setDropdown = () => {
    setTemporaryOption(selectedOption); // Reset temporary selection to confirmed option

    setShowDropdown(!showDropdown);
  };

  // Handle radio button change
  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTemporaryOptionvalue(event.target.id);
    if (event.target.id === "1") {
      setTemporaryOption("active");
    } else if (event.target.id === "0") {
      setTemporaryOption("inactive");
    } else if (event.target.id === "2") {
      setTemporaryOption("canceled");
    } else if (event.target.id === "") {
      setTemporaryOption("all");
    }
  };

  const [formValues, setFormValues] = useState({
    filterChanged: false,
    status: "",
    defaultFilter: true,
    statusFilterType: "all",
    statusFilterChanged: false,
  });

  //To clear form
  const handleClear = async () => {
    setTemporaryOption("all"); // Reset temporary selection
    setSelectedOption("all");
    setFormValues({
      filterChanged: false,
      status: "",
      defaultFilter: true,
      statusFilterType: "all",
      statusFilterChanged: false,
    });
    await setCurrentPage(1);
    setDropdown();
    setFilterClass("");
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault(); // Prevent the default form submission behavior
    setSelectedOption(temporaryOption); // Confirm the selection
    let filterChanged = false;

    if (temporaryOption !== "all") {
      filterChanged = true;
    }

    if (filterChanged === true) {
      setFilterClass("filtered");
    } else {
      setFilterClass("");
    }
    // Get form values
    setFormValues({
      filterChanged: filterChanged,
      defaultFilter: !filterChanged ? true : false,
      status: temporaryOptionvalue,
      statusFilterType: temporaryOption,
      statusFilterChanged: filterChanged,
    });
    setCurrentPage(1);
    setDropdown();
  };

  const updatePhone = async ()=>{
     try {
          setLoading(true);
          if (!PHONE_REGEX.test(currentPhonenumber)) {
          setPhoneError("Please enter a valid 10-digit phone number");
          return;
        }
        setPhoneError("");
          let phoneNumber = formatPhoneNumberforApi(currentPhonenumber);

          const payload = {
            userId:  userId,
            phoneNumber: phoneNumber
          };
    
          const res = await axios.post(CompanyApi.UPDATE_USER_PHONE_NUMBER, payload);
    
          if (res?.data?.error === 0) {
            toaster.toastSuccess(res?.data?.message);
            setShowPhoneModal(false)
            getPWAUsers(currentPage);
          } else {
            toaster.toastError(res?.data?.message || "Failed to update setting");
          }
        } catch (err) {
          toaster.toastError("API error while updating");
        } finally {
          setLoading(false);
        }
  }

  function capitalizeFirstLetter(criteria: string) {
    return criteria.charAt(0).toUpperCase() + criteria.slice(1);
  }

  const navigate = useNavigate();

  // To get path from which redirection was done
  const location = useLocation();
  const origin = location.state?.origin || "Unknown";

  const query = new URLSearchParams(location.search);
  // State to company details
  const [company, setCompany] = useState({
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  // State to display company details
  const [userDetails, setUserDetails] = useState<CarWashSubscription[]>([]);
  // State to handle search
  const [searchValue, setSearchValue] = useState<string>(
    query.get("search") || ""
  );
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(
    query.get("page") ? parseInt(query.get("page") as string, 10) : 1
  );
  const [totalPages, setTotalPages] = useState(1);
  const [record_count, setRecord_count] = useState(20);
  const params = useParams();

  // To display heading
  useEffect(() => {
    getCompanyAddress();
    if(carwash_owner)
    {
      fetchSetting();
      fetchOptSpotSetting();
      checkOptSpotFeature();
    }
    
  }, []);

  useEffect(() => {
    handleUpdate();
  }, [company]);

  useEffect(() => {
    getPWAUsers(currentPage);
  }, [currentPage, searchValue, formValues, record_count]);

  // Update the data
  const handleUpdate = () => {
    updateData({
      heading: "PWA Users",
      url: carwash_owner
        ? `/${pageURLs.dashboard}`
        : `/superadmin/${pageURLs.carwash_locations}/${params.id}`,
      showBackButton:
        props.userType === userType["CARWASH-OWNER"] ? origin === false : true,
      showMainMenu: true,
      activeLink:
        props.userType === userType["CARWASH-OWNER"]
          ? "manage-users"
          : "manage-car-wash",
    });
  };

  const getCompanyAddress = async () => {
    try {
      const id = params.id ? parseInt(params.id) : 0;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
        : await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id);
      if (data?.error === 0) {
        setCompany(data.data);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const changePhonenumber=(event:any)=>{
     const phone_value = event.target.value;
      if (!/^\d*$/.test(phone_value)) {
          event.target.value = phone_value.replace(/\D/g, '');
      }

      const { name, value } = event.target
      const formattedValue = formatPhoneNumber(value);
      setCurrentPhonenumber(formattedValue)
        if (value === "") {
          setPhoneError(""); // clear if empty
        }
  }  

  const getPWAUsers = async (pageNo = 1) => {
    try {
      setLoading(true);
      let param;
      if (searchValue.length && carwash_owner) {
        param = `?pageNo=${pageNo}&searchText=${searchValue}`;
      } else if (carwash_owner) {
        param = `?pageNo=${pageNo}`;
      } else if (searchValue.length && !carwash_owner) {
        param = `?carWashId=${params.id}&pageNo=${pageNo}&searchText=${searchValue}`;
      } else if (!carwash_owner) {
        param = `?carWashId=${params.id}&pageNo=${pageNo}`;
      }

      // Need to be considered only if filter by is done.
      if (formValues.filterChanged) {
        if (formValues.status) {
          param += "&status=" + formValues.status;
        }
      } else {
        if (formValues.status) {
          param += "&status=" + formValues.status;
        }
      }

      if (record_count) {
        param += "&pageSize=" + record_count;
      }

      const { data } = carwash_owner
        ? await axios.get(UsersApi.CARWASH_OWNER_GET_PWA_USERS + param)
        : await axios.get(UsersApi.GET_PWA_USERS + param);
      if (data?.error === 0 && Array.isArray(data?.data?.list)) {
        const PWA_user_list: UserItem[] = data.data.list;
        if (PWA_user_list.length > 0) {
          const transformedArray = PWA_user_list.map((user) => ({
            UserID: user.UserID,
            carWashLocation: user.LocationName,
            userName: user.FullName,
            phoneNumber: user.Phone,
            emailAddress: user.Email,
            subscriptionStatus: user.activeStatus,
            subscription: user.WashName,
            WashCount: user.WashCount,
            HasFamilyPlan:user.HasFamilyPlan,
            Make:user.Make,
            Model:user.Model,
            Color:user.Color,
            LicensePlateNumber:user.LicensePlateNumber

          }));
          setUserDetails(transformedArray);
          setTotalPages(Math.ceil(data.data.totalCount / data.data.limit) || 1);
        } else {
          console.log("User list is empty");
          setUserDetails([]);
        }
      } else {
        console.log("User list is not available or is not an array");
        setUserDetails([]);
      }
      setLoading(false);
    } catch (error: any) {
      console.error("Error fetching user list:", error.message);
      setLoading(false);
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  // Triggers when input is entered in search
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchValue("");
    // setCompanyDetails(companyDetailsArray);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getVisiblePageNumbers = () => {
    const totalPageNumbersToShow = 5;
    let startPage = Math.max(
      1,
      currentPage - Math.floor(totalPageNumbersToShow / 2)
    );
    let endPage = startPage + totalPageNumbersToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  // Navigate accordung to url
  const itemClick = async (url: string) => {
    navigate(url);
  };

  const [selectedUsers, setSelectedUsers] = useState<
    { value: number; label: string }[]
  >([]);

  // Handle checkbox selection
  const checkboxSelection = (user: { value: number; label: string }) => {
    setSelectedUsers((prev) =>
      prev.some((selected) => selected.value === user.value)
        ? prev.filter((selected) => selected.value !== user.value)
        : [...prev, user]
    );
  };

  // Handle "Select All" checkbox
  const allcheckboxSelection = () => {
    if (selectedUsers.length === userDetails.length) {
      setSelectedUsers([]); // Unselect all
    } else {
      setSelectedUsers(userOptions); // Select all users
    }
  };

  const [showMessagePopup, setShowMessagePopup] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [isAllUsers, setIsAllUsers] = useState<boolean>(false);

  // Handle React Select change
  const handleUserChange = (
    newValue: MultiValue<{ value: number; label: string }>
  ) => {
    setSelectedUsers([...newValue] as { value: number; label: string }[]);
  };
  // Show modal
  const showSuccessConfirmation = () => {
    if (selectedUsers.length === 0) {
      setIsAllUsers(true);
      setSelectedUsers([]);
    }
    setShowMessagePopup(true);
  };

  // Hide modal
  const hideMessageModal = () => {
    setShowMessagePopup(false);
    setMessage("");
    setIsAllUsers(false);
    setSelectedUsers([]);
    setFormData({
      to: {},
      subject: "",
      message: "",
      isAllUsers: isAllUsers,
    });
    setErrors({
      to: "",
      subject: "",
      message: "",
    });
  };

  // React Select options
  const userOptions = userDetails.map((user) => ({
    value: user.UserID,
    label: `${user.userName} (${
      user.subscriptionStatus === 1
        ? "Active"
        : user.subscriptionStatus === 0
        ? "Inactive"
        : "Canceled"
    })`,
  }));
  const editorRef = useRef<Editor | null>(null);
  // clear modal message
  const messageClear = () => {
    setMessage("");
    // Check if editor instance exists before clearing
    if (editorRef.current) {
      editorRef.current.setData("");
    }
  };

  // Initialize states for formdata
  const [formData, setFormData] = useState({
    to: {},
    subject: "",
    message: "",
    isAllUsers: isAllUsers,
  });

  // Initialise states for errors
  const [errors, setErrors] = useState({
    to: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    setFormData({
      to: selectedUsers,
      subject: formData.subject,
      message: message,
      isAllUsers: isAllUsers,
    });
  }, [selectedUsers, message]);

  // On input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // To remove validation when user enters input
    const keys = Object.keys(errors);
    if (keys.includes(name)) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }

    const newFormData = {
      ...formData,
      [name]: value,
    };

    setFormData(newFormData);
  };

  const fetchSetting = async () => {
    try {
      const res = await axios.get(CompanyApi.CARWASH_OWNER_GET_CARINFO_SETTINGS);
      if (res?.data?.error === 0 && res.data?.data?.hasCarDetails !== undefined) {
        setIsCarInfoRequired(res.data.data.hasCarDetails === 1);
      } else {
        toaster.toastError('Failed to load car info settings');
      }
    } catch (err) {
      toaster.toastError('Error fetching car info settings');
    } finally {
      setLoading(false);
    }
  };

  const checkOptSpotFeature = async () => {
    try {
      const res = await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS);
      if (res?.data?.error === 0 && res.data?.data) {
        // Check if company has OPT SPOT feature enabled
        // This might be a field like hasOptSpotFeature, isOptSpotEnabled, etc.
        // Adjust the field name based on your API response
        setHasOptSpotFeature(res.data.data.hasOptSpotFeature === 1 || res.data.data.isOptSpotEnabled === 1);
      }
    } catch (err) {
      console.error('Error checking OPT SPOT feature:', err);
    }
  };

  const fetchOptSpotSetting = async () => {
    try {
      const res = await axios.get(CompanyApi.CARWASH_OWNER_GET_OPTSPOT_SETTINGS);
      if (res?.data?.error === 0 && res.data?.data?.hasOptSpot !== undefined) {
        setIsOptSpotEnabled(res.data.data.hasOptSpot === 1);
      }
    } catch (err) {
      // Silently fail if OPT SPOT settings don't exist yet
      console.error('Error fetching OPT SPOT settings:', err);
    }
  };

  const handleOpenCarModal = async (userIdValue: number) => {
    setUserId(userIdValue);
    setUserCarInfoList([]);
    setShowCarModal(true);
    await fetchUserCarInfo(userIdValue);
  };

   const handleOpenPhoneModal = (userId:number,phoneNumber:any) => {
   setUserId(userId)
    setCurrentPhonenumber(formatPhoneNumber(phoneNumber))
    setShowPhoneModal(true);
  };

  const handleClose = () => {
    setShowCarModal(false);
    setUserCarInfoList([]);
  };


  // Triggers on message send
  const handleSend = async () => {
    if (userOptions.length === 0) {
      toaster.toastError("No user data found.");
    } else {
      try {
        setLoading(true);

        // Validate the data
        await sendMessageSchema.validate(formData, { abortEarly: false });

        let userIds: number[] = []; // Declare userIds outside

        if (Array.isArray(formData.to) && formData.to.length > 0) {
          userIds = formData.to.map((user) => Number(user.value));
        }

        const newformData = {
          userIds: isAllUsers === true ? [] : userIds,
          subject: formData.subject,
          message: formData.message,
          filterStatus:
            formValues.status === "" ? null : Number(formValues.status),
          searchText: searchValue,
          isAllUsers: isAllUsers,
        };

        // API call to send message
        try {
          const { data } = await axios.post(
            UsersApi.CARWASH_OWNER_SEND_MESSAGE,
            newformData
          );

          if (data?.error === 0) {
            hideMessageModal();
            setErrors({
              to: "",
              subject: "",
              message: "",
            });
            // show error message
            toaster.toastSuccess("The message has been sent successfully.");
          } else {
            // show error message
            toaster.toastError(data?.message);
          }
        } catch (error: any) {
          console.log(error?.response?.data?.message || error?.message);
        } finally {
          setLoading(false); // Hide loader
        }
      } catch (error) {
        const newErrors: any = {};

        //If validation fails on submit, then errors are shown
        if (error instanceof Yup.ValidationError) {
          error.inner.forEach((err) => {
            newErrors[err.path as keyof typeof formData] = err.message;
          });
          setErrors(newErrors);
        }
      } finally {
        setLoading(false);
      }
    }
  };

 const handleSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setIsCarInfoRequired(newValue);

    try {
      setLoading(true);
      const payload = {
        hasCarDetails: newValue ? 1 : 0,
      };

      const res = await axios.post(CompanyApi.CARWASH_OWNER_UPDATE_CARINFO_SETTINGS, payload);

      if (res?.data?.error === 0) {
        toaster.toastSuccess("Car info requirement updated");
      } else {
        toaster.toastError(res?.data?.message || "Failed to update setting");
      }
    } catch (err) {
      toaster.toastError("API error while updating");
    } finally {
      setLoading(false);
    }
  };

  const handleOptSpotSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setIsOptSpotEnabled(newValue);

    try {
      setLoading(true);
      const payload = {
        hasOptSpotExport: newValue ? 1 : 0,
      };

      const res = await axios.post(CompanyApi.CARWASH_OWNER_UPDATE_OPTSPOT_SETTINGS, payload);

      if (res?.data?.error === 0) {
        toaster.toastSuccess("OPT SPOT setting updated");
      } else {
        toaster.toastError(res?.data?.message || "Failed to update setting");
        // Revert on error
        setIsOptSpotEnabled(!newValue);
      }
    } catch (err) {
      toaster.toastError("API error while updating OPT SPOT setting");
      // Revert on error
      setIsOptSpotEnabled(!newValue);
    } finally {
      setLoading(false);
    }
  };

  // Convert recordCounts to an array of options
  const recordCountOptions = Object.values(recordCounts).map((count) => ({
    label: count.toString(),
    value: count,
  }));

  // Triggers whenever Record count is selected
  const handleRecordChange = (selectedOption: any) => {
    const value = selectedOption ? selectedOption.value : "";
    setRecord_count(value);
  };

  return (
    <div className="manage-carwash-container">
      <Container>
        <Breadcrumb>
          {props.userType === userType["CARWASH-OWNER"] ? null : (
            <>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(`/superadmin/${pageURLs.manage_carwash}`);
                }}
              >
                Manage Car Washes
              </Breadcrumb.Item>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(
                    `/superadmin/${pageURLs.carwash_locations}/${params.id}`
                  );
                }}
              >
                {company.companyName}
              </Breadcrumb.Item>
              <Breadcrumb.Item active>PWA Users</Breadcrumb.Item>
            </>
          )}
        </Breadcrumb>
        <div className="manage-header-block ">
          {carwash_owner && !isLocationManager ? (
            <div className="d-flex flex-column align-items-end gap-3">
              <div className="car-info-sec d-flex flex-column align-items-end">
                <div className="info-switch d-flex align-items-center">
                  <h5 className="f-bold">Require Car Information</h5>
                  <CWSwitch
                    id="statusswitch"
                    name="statusswitch"
                    checked={isCarInfoRequired}
                    onChange={handleSwitchChange}
                  />
                </div>
                <div className="desc">
                  <span className="fw-bold">Note:</span> Enable this to collect car
                  details (make, model, etc.) from users during sign up
                </div>
              </div>
              {/* Temporarily hidden OptSpot toggle
              <div className="car-info-sec d-flex flex-column align-items-end">
                <div className="info-switch d-flex align-items-center">
                  <h5 className="f-bold">Allow OptSpot Integration</h5>
                  <CWSwitch
                    id="optspotswitch"
                    name="optspotswitch"
                    checked={isOptSpotEnabled}
                    onChange={handleOptSpotSwitchChange}
                  />
                </div>
                <div className="desc">
                  <span className="fw-bold">Note:</span> Enable this to collect user details and pass them to  OptSpot
                </div>
              </div>
              */}
            </div>
          ) : ""}
          <div className="search-wrap filter-head w-100 d-flex justify-content-between align-items-center">
            <CWSearchinput
              id="search"
              name="search"
              type="text"
              placeholder={`Search by User Name, Email, Phone, ${isCarInfoRequired ? "License Plate, " : ""}Car Wash Location`}
              value={searchValue}
              onChange={handleSearch}
              onClose={handleClearSearch}
            />
            <div className="result-group d-flex align-items-center">
              <p className="title f-bold">Results for</p>
              <Dropdown
                className={"filter-dropdown " + filterClass}
                align="start"
                show={showDropdown}
                onToggle={setDropdown}
              >
                <Dropdown.Toggle
                  id="dropdown-autoclose-true"
                  onClick={setDropdown}
                >
                  Filter by
                  <FilterIcon />
                  {formValues.statusFilterType &&
                    capitalizeFirstLetter(formValues.statusFilterType)}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <h6>Filter by</h6>
                  <form onSubmit={handleSubmit}>
                    <div className="radio-field-group d-flex flex-column">
                      <CWRadio
                        id=""
                        name="subscriptionstatus"
                        label="All"
                        value=""
                        checked={temporaryOption === "all"}
                        onChange={handleRadioChange}
                      />
                      <CWRadio
                        id="1"
                        name="subscriptionstatus"
                        label="Active"
                        value="1"
                        checked={temporaryOption === "active"}
                        onChange={handleRadioChange}
                      />
                      <CWRadio
                        id="0"
                        name="subscriptionstatus"
                        label="Inactive"
                        value="0"
                        checked={temporaryOption === "inactive"}
                        onChange={handleRadioChange}
                      />
                      <CWRadio
                        id="2"
                        name="subscriptionstatus"
                        label="Canceled"
                        value="2"
                        checked={temporaryOption === "canceled"}
                        onChange={handleRadioChange}
                      />
                    </div>

                    <div className="button-block d-flex justify-content-between">
                      <Button
                        title="Reset"
                        variant="light"
                        className="light-btn"
                        onClick={() => handleClear()}
                      >
                        Reset
                      </Button>
                      <Button
                        title="Apply"
                        variant="primary"
                        className="primary-btn"
                        type="submit"
                      >
                        Apply
                      </Button>
                    </div>
                  </form>
                </Dropdown.Menu>
              </Dropdown>
              {props.userType === userType["CARWASH-OWNER"] ? (
                <Button
                  title="Message"
                  type="button"
                  variant="primary"
                  className="primary-btn d-flex align-items-center"
                  onClick={showSuccessConfirmation}
                >
                  Message
                </Button>
              ) : (
                ""
              )}
            </div>
          </div>
          <div className="show-record d-flex justify-content-end">
            <CWSelect
              id="record_count"
              name="record_count"
              options={recordCountOptions}
              label="Show Records"
              dropdownGap={0}
              dropdownPosition="auto"
              placeholder="Select"
              multi={false}
              searchable={false}
              autoFocus
              errorMessage={""}
              values={recordCountOptions.filter(
                (option) => option.value === record_count
              )}
              onChange={(selectedOptions) =>
                handleRecordChange(selectedOptions[0])
              }
              className="no-input"
            />
          </div>
        </div>
        <div className="cw-table-wrap">
          <Table responsive className="m-0">
            <thead>
              <tr>
                {carwash_owner ? (
                  <th>
                    <CWCheckbox
                      id="selectuserhead"
                      name="selectuserhead"
                      label=""
                      checked={
                        userDetails.length > 0 &&
                        selectedUsers.length === userDetails.length
                      }
                      onChange={allcheckboxSelection}
                    />
                  </th>
                ) : (
                  ""
                )}
                <th className="nowrap">Car Wash Location </th>
                <th className="nowrap">User Name</th>
                <th className="nowrap">Phone Number</th>
                <th className="nowrap">Email Address</th>
                <th>Subscription Status</th>
                {/* <th className='sort nowrap' onClick={() => sortByName("status")}>Subscription Status <SortingIcon /></th> */}
                <th>Subscription</th>
                <th>No. of Wash Count</th>
                <th>Car Info</th>
              </tr>
            </thead>
            <tbody>
              {userDetails && userDetails.length ? (
                userDetails.map((item, index) => (
                  <tr key={index}>
                    {carwash_owner ? (
                      <td>
                        <CWCheckbox
                          id="selectuser"
                          name="selectuser"
                          label=""
                          checked={selectedUsers.some(
                            (selected) => selected.value === item.UserID
                          )}
                          onChange={() =>
                            checkboxSelection({
                              value: item.UserID,
                              label: item.userName,
                            })
                          }
                        />
                      </td>
                    ) : (
                      ""
                    )}
                    <td>{item.carWashLocation}</td>
                    <td>{item.userName}</td>
                    <td className="nowrap">{item.phoneNumber}
                      {carwash_owner ? (
                      <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Edit</Tooltip>}
                        >
                          <Button
                            variant="light"
                            className="light-btn action-btn phone-edit"
                            onClick={() =>
                          handleOpenPhoneModal(item.UserID,
                            item.phoneNumber ?? ""       
                          )
                        }
                        >
                            <EditIcon />
                          </Button>
                    </OverlayTrigger>
                      ) : null}
  </td>
                    <td className="nowrap">{item.emailAddress}</td>
                    <td>
                      {/**In case of cancelled and inactive status, inactive classname is considered */}
                      <Badge
                        className={
                          item.subscriptionStatus ===
                          SubscriptionStatus.Inactive
                            ? "inactive"
                            : item.subscriptionStatus ===
                              SubscriptionStatus.Cancelled
                            ? "inactive"
                            : "active"
                        }
                      >
                        {item.subscriptionStatus === SubscriptionStatus.Inactive
                          ? "Inactive"
                          : item.subscriptionStatus ===
                            SubscriptionStatus.Cancelled
                          ? "Canceled"
                          : "Active"}
                          
                      </Badge><span className="ms-1 fw-bold">{item?.HasFamilyPlan == 1  ? "F" : ""}</span>
                    </td>
                    <td>{item.subscription}</td>
                    <td>{item.WashCount === null ? 0 : item.WashCount}</td>
                    <td>{isCarInfoRequired ? (
  <Link
    to="#"
    onClick={() => handleOpenCarModal(item.UserID)}
  >
    View Car Info
  </Link>
) : (
  "NA"
)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td style={{ textAlign: "center" }} colSpan={manageUsersTableColumnCount}>
                    No data found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {userDetails.length ? (
            <div className="mt-3 d-flex justify-content-end">
              <Pagination>
                <Pagination.First
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(1)}
                />
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                />
                {getVisiblePageNumbers().map((pageNumber) => (
                  <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === currentPage}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(totalPages)}
                />
              </Pagination>
            </div>
          ) : null}
        </div>
      </Container>
      <Modal
        show={showMessagePopup}
        onHide={hideMessageModal}
        centered
        className="campaign-modal message-modal"
      >
        <Modal.Body>
          <h5 className="fw-bold">Message</h5>
          <div className="radio-field-group d-flex">
            <CWRadio
              id="recipientType"
              name="recipientType"
              checked={!isAllUsers}
              label="Selected Users"
              onChange={() => {
                setIsAllUsers(false);
              }}
            />
            <CWRadio
              id="recipientType"
              name="recipientType"
              checked={isAllUsers}
              label={`All ${
                selectedOption === "active"
                  ? "Active "
                  : selectedOption === "inactive"
                  ? "Inactive "
                  : selectedOption === "canceled"
                  ? "Canceled "
                  : ""
              }Users`}
              onChange={() => {
                setIsAllUsers(true);
                // setSelectedUsers(userOptions);
              }}
            />
          </div>
          {!isAllUsers && (
            <div className="user-select-wrap">
              <>
                <h6 className="fw-semibold mb-1">To</h6>
                <Select
                  options={userOptions}
                  value={selectedUsers}
                  onChange={handleUserChange}
                  isMulti
                  className="multi-user-select"
                  classNamePrefix="multi-user-select"
                  placeholder="To"
                />
                <span className="error-msg">
                  {errors.to != "" && selectedUsers.length === 0
                    ? errors.to
                    : ""}
                </span>
              </>
            </div>
          )}

          <div className="input-field-wrap">
            <CWInput
              id="subject"
              name="subject"
              type="text"
              label="Subject"
              value={formData.subject}
              onChange={handleChange}
              errorMessage={""}
              isMandatory
            />
            <span className="error-msg">
              {errors.subject != "" && formData.subject === ""
                ? errors.subject
                : ""}
            </span>
          </div>

          <div className="message-input">
            <div className="head d-flex justify-content-between align-items-center">
              <h6 className="fw-semibold">Message</h6>
              <Button
                variant="outline-primary"
                className="outline-btn"
                onClick={messageClear}
              >
                Clear
              </Button>
            </div>
            <CKEditor
              editor={ClassicEditor as any}
              config={{
                licenseKey: "GPL",
                plugins: [Essentials, Paragraph, Bold, Italic, Heading],
                toolbar: [
                  "heading",
                  "|",
                  "bold",
                  "italic",
                  "underline",
                  "strikethrough",
                  "|",
                  "link",
                  "bulletedList",
                  "numberedList",
                  "blockQuote",
                  "|",
                  "undo",
                  "redo",
                  "|",
                  "alignment",
                  "fontSize",
                  "fontColor",
                  "fontBackgroundColor",
                  "highlight",
                  "codeBlock",
                  "insertTable",
                  "imageUpload",
                  "mediaEmbed",
                ],
                heading: {
                  options: [
                    {
                      model: "paragraph",
                      title: "Paragraph",
                      class: "ck-heading_paragraph",
                    },
                    {
                      model: "heading1",
                      view: "h1",
                      title: "Heading 1",
                      class: "ck-heading_heading1",
                    },
                    {
                      model: "heading2",
                      view: "h2",
                      title: "Heading 2",
                      class: "ck-heading_heading2",
                    },
                    {
                      model: "heading3",
                      view: "h3",
                      title: "Heading 3",
                      class: "ck-heading_heading3",
                    },
                    {
                      model: "heading4",
                      view: "h4",
                      title: "Heading 4",
                      class: "ck-heading_heading4",
                    },
                  ],
                },
                image: {
                  toolbar: [
                    "imageTextAlternative",
                    "toggleImageCaption",
                    "imageStyle:full",
                    "imageStyle:side",
                  ],
                },
                table: {
                  contentToolbar: [
                    "tableColumn",
                    "tableRow",
                    "mergeTableCells",
                  ],
                },
              }}
              onReady={(editor) => {
                editorRef.current = editor;
                if (message) {
                  editor.setData(message);
                }
              }}
              onChange={(event, editor) => {
                const data = editor.getData();
                setMessage(data);
              }}
            />
            <span className="error-msg">
              {errors.message != "" && message === "" ? errors.message : ""}
            </span>
          </div>
          <div className="btn-sec d-flex justify-content-end">
            <Button
              title="Not Now"
              variant="light"
              className="secondary-btn"
              onClick={hideMessageModal}
            >
              Cancel
            </Button>
            <Button
              title="Yes"
              variant="primary"
              className="primary-btn btn-main m-0"
              onClick={handleSend}
            >
              Send
            </Button>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={showCarModal}
        onHide={handleClose}
        centered
        className="carinfo-modal"
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <div className="header-block d-flex align-items-center justify-content-between">
            <h5 className="m-0 fw-bold">Car Information</h5>
            <Button
                type="button"
                title="Add Info"
                variant="primary"
                className="primary-btn d-flex align-items-center"
                onClick={handleOpenAddinfoModal}
            >
                Add Info
                <AddIcon />
            </Button>
          </div>

          {userCarInfoList.length > 0 ? (
            <Accordion defaultActiveKey="0">
              {userCarInfoList.map((car, index) => (
                <Accordion.Item
                  key={car.carId ?? index}
                  eventKey={String(index)}
                >
                  <Accordion.Header>LP number : {car.licensePlate}</Accordion.Header>
                  <Accordion.Body>
                    <p><strong>Make:</strong> {car.carMake}</p>
                    <p><strong>Model:</strong> {car.carModel}</p>
                    <p><strong>Color:</strong> {car.carColor}</p>
                    <p><strong>License Plate Number:</strong> {car.licensePlate}</p>
                    <div className="btn-sec d-flex gap-2">
                      <Button
                        type="button"
                        title="Edit Info"
                        variant="primary"
                        className="secondary-btn d-flex justify-content-center"
                        onClick={() => handleShowAddinfoModal(car)}
                      >
                        Edit Info
                      </Button>
                      <Button
                        type="button"
                        title="Delete"
                        variant="danger"
                        className="secondary-btn d-flex justify-content-center"
                        onClick={() => handleDeleteCarClick(car)}
                      >
                        Delete
                      </Button>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          ) : (
            <p className="mb-0 text-muted">No car information found.</p>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showAddinfoModal}
        onHide={handleCloseAddinfoModal}
        centered
        className="carinfo-modal"
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <h5 className="fw-bold">{editingCarId ? "Edit Info" : "Add Info"}</h5>
          <CWInput
              id="carmake"
              name="carmake"
              type="text"
              label="Make"
              value={carMake}
              onChange={(e) => setCarMake(e.target.value)}
              errorMessage={addCarInfoErrors.carMake}
              isMandatory
          />
          <CWInput
              id="carmodel"
              name="carmodel"
              type="text"
              label="Model"
              value={carModel}
              onChange={(e) => setCarModel(e.target.value)}
              errorMessage={addCarInfoErrors.carModel}
              isMandatory
          />
          <CWInput
              id="carcolor"
              name="carcolor"
              type="text"
              label="Color"
              value={carColor}
              onChange={(e) => setCarColor(e.target.value)}
              errorMessage={addCarInfoErrors.carColor}
              isMandatory
          />
          <CWInput
              id="licensenumber"
              name="licensenumber"
              type="text"
              label="License Plate Number"
              value={carLicensePlate}
              onChange={(e) => setCarLicensePlate(e.target.value)}
              errorMessage={addCarInfoErrors.carLicensePlate}
              isMandatory
          />
          <div className="btn-sec d-grid">
            <Button
              type="button"
              variant="light"
              className="secondary-btn"
              onClick={handleCloseAddinfoModal}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              className="primary-btn btn-main m-0"
              onClick={saveCarInfo}
            >
              Save
            </Button>
          </div>
        </Modal.Body>
      </Modal>   

       <Modal
        show={showPhoneModal}
        onHide={() => setShowPhoneModal(false)}
        centered
        className="carinfo-modal"
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <h5 className="fw-bold">Edit Phone number</h5>
          <div className="phone-edit-field d-flex align-items-center">
            <CWInput
              id="currentPhonenumber"
              name="currentPhonenumber"
              type="text"
              maxLength={PHONE_MAX_LENGTH}
              value={currentPhonenumber}
              errorMessage={phoneError}
              onChange={(e)=>changePhonenumber(e)}
              placeholder="Enter phone number"
            />
            <Button
              title="Update"
              variant="primary"
              className="btn primary-btn"
              onClick={()=>updatePhone()}
            >
              Update
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showCarDeleteModal}
        onHide={hideCarDeleteModal}
        centered
        className="add-modal"
      >
        <Modal.Body className="text-center">
          <h5 className="f-bold text-center">
            Are you sure you want to delete the car with LP number:{" "}
            {carToDelete?.licensePlate}?
          </h5>
          <div className="btn-sec d-grid">
            <Button
              variant="light"
              className="secondary-btn"
              onClick={hideCarDeleteModal}
            >
              Not Now
            </Button>
            <Button
              variant="primary"
              className="primary-btn btn-main m-0"
              onClick={confirmDeleteUserCarInfo}
            >
              Yes
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {loading && <SmallLoader />}
    </div>
  );
};

export default ManageUsers;
